import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Truck, BadgePercent, Boxes, Plus, Check, Minus, ShoppingBag } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ProductCard } from "../components/ProductCard";
import { QuoteModal } from "../components/QuoteModal";
import { SEO, productSEO } from "../components/SEO";
import { getProduct, resolveImg } from "../lib/api";
import { useCart } from "../lib/cart";

const titleCase = (slug) => (slug || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const Zoom = ({ src, alt }) => {
  const ref = useRef(null);
  const [style, setStyle] = useState({});
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setStyle({ transformOrigin: `${x}% ${y}%`, transform: "scale(2)" });
  };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => setStyle({ transform: "scale(1)" })} data-testid="product-zoom"
      className="relative aspect-square rounded-2xl border border-vr-border bg-[#e6f4fc] overflow-hidden cursor-zoom-in flex items-center justify-center p-8">
      {src ? <img src={src} alt={alt} style={style} className="max-h-full max-w-full object-contain transition-transform duration-200" /> : <div className="w-full h-full bg-vr-bg2 rounded-xl" />}
    </div>
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const { items: cartItems, add: addToCart, setOpen: openCart } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null); setNotFound(false);
    setQty(1);
    getProduct(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div data-testid="product-notfound" className="min-h-screen grid place-items-center pt-40"><div className="text-center"><h1 className="font-display font-bold text-4xl text-vr-ink mb-4">Product not found</h1><Link to="/products" className="text-vr-brand font-semibold underline">All products</Link></div></div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-vr-dim pt-40">Loading…</div>;

  const { product: p, related, frequently_bought } = data;
  const subName = p.subcategory_name || titleCase(p.subcategory);
  const img = p.images && p.images.length ? resolveImg(p.images[0]) : null;

  return (
    <main data-testid="product-detail-page" className="pt-[104px] bg-vr-bg">
      <SEO {...productSEO(p)} />
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-8">
        <Breadcrumbs items={[{ label: "Products", to: "/products" }, { label: titleCase(p.category), to: `/category/${p.category}` }, { label: p.subcategory_name || titleCase(p.subcategory), to: `/category/${p.category}?sub=${p.subcategory}` }, { label: p.name }]} />

        <div className="mt-8 grid lg:grid-cols-2 gap-10 md:gap-14 items-start">
          <div className="lg:sticky lg:top-28">
            <Zoom src={img} alt={p.name} />
            <p className="mt-3 text-center text-xs text-vr-dim">Hover image to zoom</p>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-vr-brand bg-vr-brand/10 px-2.5 py-1 rounded">SKU: {p.sku}</span>
              {p.bestseller && <span className="text-xs font-bold uppercase tracking-wider bg-vr-navy text-white px-2.5 py-1 rounded-full">Best Seller</span>}
            </div>
            <h1 className="mt-4 font-display font-extrabold tracking-tight text-3xl md:text-5xl text-vr-ink leading-[1.05]">{p.name}</h1>
            <p className="mt-5 text-vr-dim leading-relaxed">{p.description}</p>

            {/* key specs */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[["Inner Packing", p.inner_packing], ["Outer Packing", p.outer_packing], ["Pallet Packing", p.pallet_packing], ["Unit of Measure", p.unit_measure]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="rounded-xl border border-vr-border bg-white p-4">
                  <div className="text-xs uppercase tracking-wider text-vr-dim">{k}</div>
                  <div className="mt-1 font-semibold text-vr-ink">{v}</div>
                </div>
              ))}
            </div>

            {/* CTAs */}
            {(() => {
              const inCart = cartItems.some((i) => i.sku === p.sku);
              const dec = () => setQty((q) => Math.max(1, Number(q) - 1));
              const inc = () => setQty((q) => Math.min(9999, Number(q) + 1));
              return (
                <div className="mt-9 space-y-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="inline-flex items-center bg-white rounded-full border border-vr-border overflow-hidden" data-testid="qty-stepper">
                      <button type="button" onClick={dec} aria-label="Decrease" data-testid="qty-dec" className="w-10 h-11 grid place-items-center text-vr-dim hover:text-vr-brand transition-colors">
                        <Minus size={16} />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={qty}
                        onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                        data-testid="qty-input"
                        className="w-14 text-center font-semibold text-vr-ink focus:outline-none bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        aria-label="Quantity"
                      />
                      <button type="button" onClick={inc} aria-label="Increase" data-testid="qty-inc" className="w-10 h-11 grid place-items-center text-vr-dim hover:text-vr-brand transition-colors">
                        <Plus size={16} />
                      </button>
                    </div>
                    <span className="text-xs text-vr-dim">
                      {p.outer_packing ? `${p.outer_packing} per unit` : "Sold by the carton"}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      data-testid="add-to-quote-btn"
                      onClick={() => { addToCart(p, String(qty)); }}
                      className={`group inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-colors ${inCart ? "bg-vr-brand/10 text-vr-brand border border-vr-brand" : "bg-vr-navy text-white hover:bg-vr-brand"}`}
                    >
                      {inCart ? (<><Check size={18} /> In your quote list · Update</>) : (<><Plus size={18} /> Add to Quote</>)}
                    </button>

                    {inCart && (
                      <button
                        type="button"
                        data-testid="view-quote-list-btn"
                        onClick={() => openCart(true)}
                        className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-vr-brand text-vr-brand font-semibold hover:bg-vr-brand hover:text-white transition-colors"
                      >
                        <ShoppingBag size={18} /> View Quote List ({cartItems.length})
                      </button>
                    )}

                    <button
                      type="button"
                      data-testid="request-quote-btn"
                      onClick={() => setQuoteOpen(true)}
                      className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-vr-navy/25 text-vr-navy font-semibold hover:border-vr-brand hover:text-vr-brand transition-colors"
                    >
                      <BadgePercent size={18} /> Request a Quote (this item)
                    </button>

                    <Link
                      to="/trade-account"
                      data-testid="detail-trade-cta"
                      className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-vr-navy/25 text-vr-navy font-semibold hover:border-vr-brand hover:text-vr-brand transition-colors"
                    >
                      <FileText size={18} /> Open Trade Account
                    </Link>
                  </div>

                  <p className="text-xs text-vr-dim">
                    Tip: click <span className="font-semibold text-vr-ink">Add to Quote</span> on multiple products, then submit them all as a single enquiry from your quote list.
                  </p>
                </div>
              );
            })()}

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-vr-dim">
              <span className="flex items-center gap-2"><Truck size={15} className="text-vr-brand" /> Nationwide delivery</span>
              <span className="flex items-center gap-2"><Boxes size={15} className="text-vr-brand" /> Sold by the carton</span>
            </div>
          </motion.div>
        </div>

        {/* Specifications table */}
        <div className="mt-16 max-w-3xl">
          <h2 className="font-display font-bold text-2xl text-vr-ink mb-5">Specifications</h2>
          <div className="rounded-2xl border border-vr-border overflow-hidden bg-white">
            {Object.entries(p.specifications).map(([k, v], i) => (
              <div key={k} className={`flex ${i % 2 ? "bg-vr-bg2" : "bg-white"}`}>
                <div className="w-1/2 md:w-1/3 px-5 py-3.5 text-sm font-medium text-vr-dim border-r border-vr-border">{k}</div>
                <div className="flex-1 px-5 py-3.5 text-sm text-vr-ink">{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Frequently bought together */}
        {frequently_bought?.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display font-bold text-2xl text-vr-ink mb-6">Frequently bought together</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {frequently_bought.map((fp, i) => <ProductCard key={fp.sku} p={fp} index={i} />)}
            </div>
          </div>
        )}

        {/* Related */}
        {related?.length > 0 && (
          <div className="mt-20 pb-8">
            <h2 className="font-display font-bold text-2xl text-vr-ink mb-6">Related products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {related.map((rp, i) => <ProductCard key={rp.sku} p={rp} index={i} />)}
            </div>
          </div>
        )}
      </div>
      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} product={p} />
    </main>
  );
}
